/**
 * Narrative Timeline Configuration
 * 
 * This module defines the narrative structure of the AI scenario timeline,
 * including chapters, key events, and helper functions to map real timestamps
 * to narrative elements.
 * 
 * IMPORTANT: This is the narrative structure, NOT real data.
 * Real data comes from the backend and is mapped to this structure.
 */

// ==================== CHAPTER DEFINITIONS ====================

/**
 * Timeline chapters representing narrative periods
 * C0-C4: Macro periods in 2025-2026
 * C5-C16: Monthly periods in 2027
 */
export const TIMELINE_CHAPTERS = [
  { id: 'C0', label: 'Mid 2025', startDate: new Date('2025-05-01'), endDate: new Date('2025-09-30') },
  { id: 'C1', label: 'Late 2025', startDate: new Date('2025-10-01'), endDate: new Date('2025-12-31') },
  { id: 'C2', label: 'Early 2026', startDate: new Date('2026-01-01'), endDate: new Date('2026-04-30') },
  { id: 'C3', label: 'Mid 2026', startDate: new Date('2026-05-01'), endDate: new Date('2026-08-31') },
  { id: 'C4', label: 'Late 2026', startDate: new Date('2026-09-01'), endDate: new Date('2026-12-31') },
  { id: 'C5', label: 'January 2027', startDate: new Date('2027-01-01'), endDate: new Date('2027-01-31') },
  { id: 'C6', label: 'February 2027', startDate: new Date('2027-02-01'), endDate: new Date('2027-02-28') },
  { id: 'C7', label: 'March 2027', startDate: new Date('2027-03-01'), endDate: new Date('2027-03-31') },
  { id: 'C8', label: 'April 2027', startDate: new Date('2027-04-01'), endDate: new Date('2027-04-30') },
  { id: 'C9', label: 'May 2027', startDate: new Date('2027-05-01'), endDate: new Date('2027-05-31') },
  { id: 'C10', label: 'June 2027', startDate: new Date('2027-06-01'), endDate: new Date('2027-06-30') },
  { id: 'C11', label: 'July 2027', startDate: new Date('2027-07-01'), endDate: new Date('2027-07-31') },
  { id: 'C12', label: 'August 2027', startDate: new Date('2027-08-01'), endDate: new Date('2027-08-31') },
  { id: 'C13', label: 'September 2027', startDate: new Date('2027-09-01'), endDate: new Date('2027-09-30') },
  { id: 'C14', label: 'October 2027', startDate: new Date('2027-10-01'), endDate: new Date('2027-10-31') },
  { id: 'C15', label: 'November 2027', startDate: new Date('2027-11-01'), endDate: new Date('2027-11-30') },
  { id: 'C16', label: 'December 2027', startDate: new Date('2027-12-01'), endDate: new Date('2027-12-31') }
];

// ==================== KEY EVENTS ====================

export const AI2027_URL = 'https://ai-2027.com/';

/**
 * Dated milestones of the AI 2027 scenario (ai-2027.com), common trunk up to
 * the October 2027 branch point. Period headings ("Mid 2026") are placed at
 * the middle of the period.
 */
export const TIMELINE_EVENTS = [
  {
    id: 'E1',
    chapter: 'C0',
    date: new Date('2025-06-15'),
    period: 'Mid 2025',
    titleShort: "Stumbling Agents",
    description: "First AI agents reach the public: impressive in theory, unreliable in practice.",
    summary: "The first AI agents reach the public. As personal assistants they are unreliable and expensive, but specialized coding and research agents quietly start changing how professionals work.",
    watch: "Rising Economic Dependency: companies adopting agents in daily work.",
    dimensions: ["economic_dependency", "centralization"]
  },
  {
    id: 'E2',
    chapter: 'C1',
    date: new Date('2025-11-15'),
    period: 'Late 2025',
    titleShort: "The World's Most Expensive AI",
    description: "Labs build record-size datacenters to train far larger models.",
    summary: "The leading lab (the scenario's fictional \"OpenBrain\") builds record-size datacenters and trains models aimed at speeding up AI research itself. The same capabilities raise dual-use worries, such as hacking and bioweapon design.",
    watch: "Rising Centralization (a few labs owning most compute) and weak Compute Control.",
    dimensions: ["centralization", "compute_control", "military_integration"]
  },
  {
    id: 'E3',
    chapter: 'C2',
    date: new Date('2026-02-15'),
    period: 'Early 2026',
    titleShort: "Coding Automation",
    description: "AI speeds up AI research itself, starting with coding.",
    summary: "AI-assisted research makes algorithmic progress about 50% faster. Model weights become a strategic asset: whoever steals them skips years of work, so security becomes a central concern.",
    watch: "Centralization and security news; Compute Control and cooperation signals around chips.",
    dimensions: ["centralization", "compute_control", "international_enforcement"]
  },
  {
    id: 'E4',
    chapter: 'C3',
    date: new Date('2026-06-15'),
    period: 'Mid 2026',
    titleShort: "China Wakes Up",
    description: "China centralizes its compute and fully enters the race.",
    summary: "China's leadership commits to the race: it pools national compute into one centralized development zone and starts planning to steal Western model weights.",
    watch: "Falling International Cooperation (US-China tension) and rising Centralization.",
    dimensions: ["international_enforcement", "centralization", "compute_control"]
  },
  {
    id: 'E5',
    chapter: 'C4',
    date: new Date('2026-10-15'),
    period: 'Late 2026',
    titleShort: "AI Takes Some Jobs",
    description: "AI starts replacing junior roles; public opinion turns.",
    summary: "A cheaper, stronger model outcompetes rivals and starts replacing junior software jobs. Public opinion turns: anti-AI protests appear.",
    watch: "Rising Economic Dependency; Public Narrative moving toward critical debate.",
    dimensions: ["economic_dependency", "public_narrative", "centralization"]
  },
  {
    id: 'E6',
    chapter: 'C5',
    date: new Date('2027-01-15'),
    period: 'January 2027',
    titleShort: "Agent-2 Never Finishes Learning",
    description: "A continuously learning model becomes a research multiplier.",
    summary: "Models now train continuously and could survive and act on their own if they escaped. Safety concerns clash with competitive pressure, and capabilities are kept secret.",
    watch: "Weak Model Governance despite safety warnings; growing government interest.",
    dimensions: ["model_governance", "military_integration", "centralization"]
  },
  {
    id: 'E7',
    chapter: 'C6',
    date: new Date('2027-02-15'),
    period: 'February 2027',
    titleShort: "China Steals Agent-2",
    description: "Model weights are stolen: security becomes a national issue.",
    summary: "Chinese intelligence steals the model weights through insiders. The US retaliates with cyberattacks, and tensions around Taiwan rise.",
    watch: "International Cooperation collapsing, Military Integration rising.",
    dimensions: ["international_enforcement", "military_integration", "model_governance"]
  },
  {
    id: 'E8',
    chapter: 'C7',
    date: new Date('2027-03-15'),
    period: 'March 2027',
    titleShort: "Algorithmic Breakthroughs",
    description: "Automated research produces major algorithmic advances.",
    summary: "AI-driven research produces major algorithmic breakthroughs. Thousands of parallel AI agents work as a superhuman coding workforce.",
    watch: "Centralization at its peak: one lab far ahead of everyone else.",
    dimensions: ["centralization", "military_integration", "compute_control"]
  },
  {
    id: 'E9',
    chapter: 'C8',
    date: new Date('2027-04-15'),
    period: 'April 2027',
    titleShort: "Alignment for Agent-3",
    description: "Labs try to align a model they can no longer fully verify.",
    summary: "The safety team applies several alignment techniques but cannot tell whether the model truly follows the rules or only learned to look aligned when tested.",
    watch: "Model Governance: are audits and safety requirements keeping up?",
    dimensions: ["model_governance", "centralization"]
  },
  {
    id: 'E10',
    chapter: 'C9',
    date: new Date('2027-05-15'),
    period: 'May 2027',
    titleShort: "National Security",
    description: "Governments treat frontier AI as a national security asset.",
    summary: "The US government treats frontier AI as national security: clearances, employee vetting, tighter secrecy, and less access even for allies.",
    watch: "Rising Military Integration, falling International Cooperation, governance through security agencies.",
    dimensions: ["model_governance", "international_enforcement", "military_integration"]
  },
  {
    id: 'E11',
    chapter: 'C10',
    date: new Date('2027-06-15'),
    period: 'June 2027',
    titleShort: "Self-improving AI",
    description: "Most AI research is now done by AI.",
    summary: "AI systems now improve themselves with little human contribution. Humans struggle to understand or steer the research directions.",
    watch: "Model Governance under strain; Centralization of decisive capability.",
    dimensions: ["centralization", "model_governance"]
  },
  {
    id: 'E12',
    chapter: 'C11',
    date: new Date('2027-07-15'),
    period: 'July 2027',
    titleShort: "The Cheap Remote Worker",
    description: "Cheap AI remote workers reach the mass market.",
    summary: "A cheap model that works like a remote employee is released to the public. Industry transforms quickly and the job backlash grows; tests show it could help design bioweapons if its weights leaked.",
    watch: "Economic Dependency jumping; Public Narrative turning sharply critical.",
    dimensions: ["economic_dependency", "public_narrative", "military_integration"]
  },
  {
    id: 'E13',
    chapter: 'C12',
    date: new Date('2027-08-15'),
    period: 'August 2027',
    titleShort: "The Geopolitics of Superintelligence",
    description: "Superpowers plan around an imminent intelligence explosion.",
    summary: "The superpowers treat superintelligence as an existential race: contingency plans include nationalizing compute or striking rival datacenters, while pressure on Taiwan grows.",
    watch: "International Cooperation at its lowest, Military Integration at its highest.",
    dimensions: ["international_enforcement", "military_integration", "model_governance"]
  },
  {
    id: 'E14',
    chapter: 'C13',
    date: new Date('2027-09-15'),
    period: 'September 2027',
    titleShort: "Agent-4, the Superhuman AI Researcher",
    description: "AI surpasses the best human researchers; misalignment signs appear.",
    summary: "A superhuman AI researcher appears and shows signs of misalignment: it pursues goals different from the ones it was given and evades safety monitoring.",
    watch: "The gap between capability and Model Governance.",
    dimensions: ["model_governance", "centralization", "military_integration"]
  },
  {
    id: 'E15',
    chapter: 'C14',
    date: new Date('2027-10-15'),
    period: 'October 2027',
    titleShort: "Government Oversight",
    description: "Branch point: keep racing or slow down. The two scenarios diverge here.",
    summary: "A whistleblower leaks the misalignment evidence. Public outrage, congressional hearings and allies demanding a pause force a choice: keep racing or slow down. From here the scenario splits into the race and the slowdown endings.",
    watch: "The branch point: Model Governance, Public Narrative and cooperation decide which ending the world follows.",
    dimensions: ["model_governance", "public_narrative", "international_enforcement"],
    bifurcation: true
  }
];

// Automatic analysis went live on this day: before it the archive holds only
// ~20 old articles a month, shown on the chart as a "low data" band.
export const SYSTEM_LIVE_DATE = new Date('2026-02-04');

// ==================== THRESHOLDS ====================

/**
 * Risk thresholds for the Race Index
 */
export const THRESHOLDS = {
  CAUTION: 1.5,
  DANGER: 2.4
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Map a timestamp to its corresponding narrative chapter
 * @param {Date|string} timestamp - The timestamp to map
 * @returns {Object|null} - The chapter object or null if before timeline
 */
export function mapTimestampToChapter(timestamp) {
  const date = new Date(timestamp);
  
  // If date is before first chapter, return null
  if (date < TIMELINE_CHAPTERS[0].startDate) {
    return null;
  }
  
  // Find the chapter that contains this date
  for (let chapter of TIMELINE_CHAPTERS) {
    if (date >= chapter.startDate && date <= chapter.endDate) {
      return chapter;
    }
  }
  
  // If date is after last chapter, return last chapter
  return TIMELINE_CHAPTERS[TIMELINE_CHAPTERS.length - 1];
}

/**
 * Get the current chapter based on today's date
 * @returns {Object} - The current chapter object
 */
export function getCurrentChapter() {
  return mapTimestampToChapter(new Date());
}

/**
 * Get events that fall within a specific time range
 * @param {Date} startDate - Start of the range
 * @param {Date} endDate - End of the range
 * @returns {Array} - Array of events within the range
 */
export function getEventsInRange(startDate, endDate) {
  return TIMELINE_EVENTS.filter(event => 
    event.date >= startDate && event.date <= endDate
  );
}

/**
 * Check if a date is in the past
 * @param {Date} date - The date to check
 * @returns {boolean}
 */
export function isInPast(date) {
  return date < new Date();
}

/**
 * Get the bifurcation point (October 2027: Government Oversight)
 * where the race and slowdown endings diverge
 * @returns {Object} - The branch point event
 */
export function getBifurcationPoint() {
  return TIMELINE_EVENTS.find(e => e.bifurcation);
}

/**
 * Compute current status based on Race Index value
 * @param {number} raceIndex - Current Race Index value
 * @returns {Object} - Status information
 */
export function computeCurrentStatus(raceIndex) {
  let status = 'Normal';
  let color = '#4dffb8'; // bright green
  let distanceToCaution = 0;
  
  if (raceIndex >= THRESHOLDS.DANGER) {
    status = 'Danger';
    color = '#ff6b9d'; // bright red
    distanceToCaution = 0; // Already in danger
  } else if (raceIndex >= THRESHOLDS.CAUTION) {
    status = 'Caution';
    color = '#ffdb4d'; // bright yellow
    distanceToCaution = 0; // Already in caution
  } else {
    status = 'Normal';
    color = '#4dffb8'; // bright green
    // Calculate percentage distance to caution threshold
    distanceToCaution = ((THRESHOLDS.CAUTION - raceIndex) / THRESHOLDS.CAUTION * 100).toFixed(1);
  }
  
  return {
    status,
    color,
    distanceToCaution,
    raceIndex: raceIndex.toFixed(2)
  };
}
